import { useMemo, useState } from "react";
import { usuarioArbolOrganizacion } from "../../api/usuariosOrganizacion.api";
import type { AccesosOrgPayload, CompaniaArbol, ModoAcceso, OpcionSede } from "../../types/accesosOrganizacion.types";
import { arbolDesdeSedesLegado, etiquetaSede, indexarSedes, sedesDeArbol } from "../../utils/arbolOrganizacion";
import { mismasSedes, sedesVigentes } from "../../utils/accesoVigente";
import { useConfiguracionSimple } from "./useConfiguracionSimple";

const codigosDe = (lista: OpcionSede[]) => lista.map((s) => Number(s.value)).filter(Number.isInteger)

// La compañía del usuario va primero en el árbol completo.
const primeroLaCompania = (arbol: CompaniaArbol[], compania: string) =>
    [...arbol].sort((a, b) => Number(b.codigo === compania) - Number(a.codigo === compania))

// Modal "Accesos por organización". El tipo de configuración se elige dentro del modal:
//   grupo        -> Simple: compañía, sucursal y sedes con los selects de siempre; el
//                   alcance lo define el grupo (como hasta hoy).
//   organizacion -> Por organización: el usuario ve exactamente las sedes del árbol.
// Para un usuario simple, el árbol parte de lo que hoy ve (sedes_vigentes).
export const useAccesosOrganizacion = ({
    usuario, accion, token, watch, setValue, listaCompanias, listaSucursales, listaSedes,
    sedesSeleccionadas, setSedesSeleccionadas, setErrorSedes,
    obtenerSucursalesPorCompania, obtenerSedesPorSucursal,
}: any) => {
    const [abierto, setAbierto] = useState(false)
    const [modo, setModo] = useState<ModoAcceso>(usuario?.accesos_org === "S" ? "organizacion" : "grupo")
    const [modoBorrador, setModoBorrador] = useState<ModoAcceso>(modo)
    const [borrador, setBorrador] = useState<Set<number>>(new Set())
    const [principal, setPrincipal] = useState<number | null>(null)
    const [arbolOrg, setArbolOrg] = useState<CompaniaArbol[] | null>(null)
    const simple = useConfiguracionSimple({ watch, setValue, obtenerSucursalesPorCompania, obtenerSedesPorSucursal })

    const compania = `${watch("compania") ?? ""}`
    const sucursal = `${watch("sucursal") ?? ""}`

    // Sedes de la familia de la compañía: las únicas que admite la configuración simple.
    const arbolLegado = useMemo(() => {
        const comp = (listaCompanias || []).find((c: any) => `${c?.id}` === compania)
        const suc = (listaSucursales || []).find((s: any) => `${s?.CompCod}` === sucursal)
        return arbolDesdeSedesLegado(listaSedes, { codigo: compania, nombre: comp?.tipo || compania },
            { codigo: sucursal, nombre: suc?.CompNom || sucursal })
    }, [listaSedes, listaCompanias, listaSucursales, compania, sucursal])
    const familia = useMemo(() => new Set(sedesDeArbol(arbolLegado)), [arbolLegado])

    // Árbol completo (todas las compañías); si no se pudo cargar, el de la compañía.
    const arbol = useMemo(
        () => (arbolOrg && arbolOrg.length ? primeroLaCompania(arbolOrg, compania) : arbolLegado),
        [arbolOrg, arbolLegado, compania],
    )
    const indice = useMemo(() => indexarSedes(arbol), [arbol])

    // Lo que el usuario ve hoy, mientras no se hayan cambiado sus sedes en el formulario.
    const guardadas = useMemo(
        () => (usuario?.sedes || []).map((s: any) => Number(s?.SedCod)).filter(Number.isInteger),
        [usuario],
    )
    const vigentes = useMemo(() => {
        const lista = accion === "agregar" ? null : sedesVigentes(usuario)
        return lista && mismasSedes(codigosDe(sedesSeleccionadas), guardadas) ? lista : null
    }, [accion, usuario, sedesSeleccionadas, guardadas])
    const propias = sedesSeleccionadas.filter((s: OpcionSede) => familia.has(Number(s.value)))

    const cargarArbolOrg = async () => {
        if (arbolOrg) return
        const res = await usuarioArbolOrganizacion({ token })
        setArbolOrg(res?.arbol || [])
    }

    const abrir = () => {
        const actuales = codigosDe(sedesSeleccionadas)
        const inicial = modo === "grupo" && vigentes ? vigentes : actuales
        simple.iniciar(modo === "grupo" ? sedesSeleccionadas : propias)
        setBorrador(new Set(inicial))
        setPrincipal(actuales.find((c) => inicial.includes(c)) ?? inicial[0] ?? null)
        setModoBorrador(modo)
        cargarArbolOrg()
        setAbierto(true)
    }

    const marcar = (codigos: number[], valor: boolean) => {
        const nuevo = new Set(borrador)
        codigos.forEach((c) => (valor ? nuevo.add(c) : nuevo.delete(c)))
        setBorrador(nuevo)
        if (principal === null || !nuevo.has(principal)) setPrincipal([...nuevo][0] ?? null)
    }

    // Pasar a "Por organización" sin nada marcado parte de lo elegido en "Simple".
    const cambiarModo = (nuevo: ModoAcceso) => {
        setModoBorrador(nuevo)
        if (nuevo !== "organizacion" || borrador.size > 0) return
        const desdeSimple = codigosDe(simple.sedes)
        setBorrador(new Set(desdeSimple))
        setPrincipal(desdeSimple[0] ?? null)
    }

    const cancelar = () => {
        simple.restaurar()
        setAbierto(false)
    }

    // Sedes del árbol en su orden, con la principal primero.
    const seleccionArbol = (): OpcionSede[] => {
        const enArbol = sedesDeArbol(arbol)
        const fuera = [...borrador].filter((c) => !enArbol.includes(c))
        const lista = [...enArbol.filter((c) => borrador.has(c)), ...fuera]
        const primera = principal !== null && lista.includes(principal) ? principal : lista[0]
        const ordenada = [primera, ...lista.filter((c) => c !== primera)].filter((c) => c !== undefined)
        const previas = new Map(sedesSeleccionadas.map((s: OpcionSede) => [Number(s.value), s.label]))
        return ordenada.map((c) => ({ value: c, label: etiquetaSede(indice, c, previas.get(c) as string) }))
    }

    // Al AGREGAR por organización, la compañía y sucursal del usuario salen de su sede
    // principal (sucursal real de esa sede). Al editar se conservan las guardadas.
    const asignarDesdePrincipal = async (sedePrincipal: number) => {
        const nodo = indice.get(sedePrincipal)
        if (accion !== "agregar" || !nodo) return
        setValue("compania", nodo.compania.codigo)
        await obtenerSucursalesPorCompania(nodo.compania.codigo)
        setValue("sucursal", nodo.sucursal.codigo)
    }

    const aplicar = () => {
        const seleccion = modoBorrador === "grupo" ? simple.sedes : seleccionArbol()
        if (seleccion.length === 0) return false
        if (modoBorrador === "organizacion") asignarDesdePrincipal(Number(seleccion[0].value))
        setSedesSeleccionadas(seleccion)
        setModo(modoBorrador)
        setErrorSedes(false)
        setAbierto(false)
        return true
    }

    // 'S' = por organización; 'N' solo si antes estaba marcado; si no, se omite.
    const accesosOrg: AccesosOrgPayload =
        modo === "organizacion" ? "S" : usuario?.accesos_org === "S" ? "N" : undefined

    return {
        abierto, abrir, cancelar, aplicar, modo, modoBorrador, cambiarModo,
        arbol, indice, borrador, marcar, principal, setPrincipal, compania, accion,
        cargandoOrg: arbolOrg === null,
        simple, listaCompanias, listaSucursales, listaSedes, sucursal,
        desdeVigentes: modo === "grupo" && vigentes !== null,
        sedesDescartadas: modo === "organizacion" ? sedesSeleccionadas.length - propias.length : 0,
        totalBorrador: modoBorrador === "grupo" ? simple.sedes.length : borrador.size,
        sedesQueVe: modo === "grupo" && vigentes ? vigentes.length : null,
        accesosOrg,
    }
}
